<template>
  <!-- App Bar -->
  <v-app-bar app fixed scroll-behavior="elevate" class="app-bar-bg px-4">
    <div class="d-flex align-center">
      <v-app-bar-nav-icon
        variant="text"
        class="nav-icon"
        @click.stop="toggleDrawer"
      />
      <v-toolbar-title class="site-title font-weight-bold ml-4 mb-0">
        exships
      </v-toolbar-title>
    </div>

    <template #append>
      <!-- テーマトグル: 太陽↔月がモーフする単一ボタン。状態は aria-checked が伝える -->
      <button
        type="button"
        role="switch"
        class="theme-toggle"
        :class="{ 'is-dark': isDark }"
        :aria-checked="isDark"
        aria-label="ダークモード"
        @click="toggle"
      >
        <span class="theme-toggle-disc" aria-hidden="true">
          <svg
            class="theme-toggle-svg"
            viewBox="0 0 20 20"
            width="20"
            height="20"
            fill="currentColor"
            focusable="false"
          >
            <mask id="theme-toggle-moon-mask">
              <rect x="0" y="0" width="20" height="20" fill="white" />
              <circle class="moon-cut" cx="11" cy="3" r="8" fill="black" />
            </mask>
            <circle class="sun-moon" cx="10" cy="10" r="8" mask="url(#theme-toggle-moon-mask)" />
            <g class="sun-rays">
              <circle class="sun-ray" cx="18" cy="10" r="1.5" />
              <circle class="sun-ray" cx="14" cy="16.928" r="1.5" />
              <circle class="sun-ray" cx="6" cy="16.928" r="1.5" />
              <circle class="sun-ray" cx="2" cy="10" r="1.5" />
              <circle class="sun-ray" cx="6" cy="3.1718" r="1.5" />
              <circle class="sun-ray" cx="14" cy="3.1718" r="1.5" />
            </g>
          </svg>
        </span>
      </button>
    </template>
  </v-app-bar>

  <!-- Navigation Drawer -->
  <v-navigation-drawer v-model="drawer">
    <v-list class="drawer-list">
      <v-list-item
        v-for="item in navItems"
        :key="item.value"
        :to="item.to"
        class="drawer-item"
        @click="handleNavClick(item)"
      >
        <div class="drawer-item-inner">
          <v-icon size="20" class="drawer-icon">{{ item.icon }}</v-icon>
          <span class="drawer-label">{{ item.title }}</span>
        </div>
      </v-list-item>
    </v-list>
  </v-navigation-drawer>

  <v-main>
    <slot />
    <v-footer class="v-footer">
      <v-container class="text-center">
        <nav class="footer-links">
          <router-link to="/privacy_policy" class="footer-link">
            プライバシーポリシー
          </router-link>
          <span class="footer-divider">|</span>
          <router-link to="/terms_of_use" class="footer-link">
            利用規約
          </router-link>
        </nav>
        © 2020 - {{ currentYear }} exships LLC.
      </v-container>
    </v-footer>
  </v-main>

  <BackToTop />
  <RouteProgress />
</template>

<script setup>
import { useDrawer } from "@/composables/useDrawer";
import { useColorTheme } from "@/composables/useColorTheme";
import { navItems } from "@/constants/navItems";
import BackToTop from "@/components/BackToTop.vue";
import RouteProgress from "@/components/RouteProgress.vue";

const { drawer, toggleDrawer, handleNavClick } = useDrawer();
const { isDark, toggle } = useColorTheme();
const currentYear = new Date().getFullYear();
</script>

<style scoped>
.app-bar-bg {
  background: var(--c-navbar) !important;
  backdrop-filter: blur(8px);
  border-bottom: 1px solid var(--c-border) !important;
  /* ページ遷移（View Transitions）時にナビバーを静止させる */
  view-transition-name: app-navbar;
}

.nav-icon {
  color: var(--c-navbar-text) !important;
}

.site-title {
  color: var(--c-navbar-text) !important;
}

/* ===== Drawer ===== */
.drawer-list {
  padding: 12px 0;
}

.drawer-item {
  border-radius: 10px;
  margin-bottom: 4px;
  transition: transform 0.15s ease;
}
.drawer-item:active {
  transform: scale(0.98);
  transition-duration: 0.08s;
}

.drawer-item-inner {
  display: flex;
  align-items: center;
  gap: 32px;
  padding: 4px 12px;
  width: 100%;
}

.drawer-icon {
  width: 24px;
  flex-shrink: 0;
  display: flex;
  justify-content: center;
  color: var(--c-drawer-text) !important;
}

.drawer-label {
  font-size: 0.95rem;
  font-weight: 500;
  color: var(--c-drawer-text);
}

/* ===== Footer ===== */
.v-footer {
  background-color: var(--c-footer) !important;
  color: var(--c-footer-text) !important;
  border-top: 1px solid var(--c-border-subtle) !important;
}

.footer-links {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  margin-bottom: 8px;
}

.footer-link {
  font-size: 0.82rem;
  color: var(--c-footer-text);
  text-decoration: none;
  transition: color 0.2s ease;
}
.footer-link:hover {
  color: var(--c-text-1);
  text-decoration: underline;
  text-underline-offset: 3px;
}

.footer-divider {
  color: var(--c-text-5);
  font-size: 0.75rem;
}

/* ===== Theme Toggle ===== */
/* uiverse.io のテーマスイッチ系パターン(MIT, Type-Delta)をデザイントークンに合わせて移植。
   太陽の円をマスク円で欠かせて月にし、切り替え時にマスク円の移動・本体の縮小・
   光線の出現を同時に走らせてモーフさせる。元スニペットは checkbox の :checked で
   状態を持つが、ここでは isDark に連動した .is-dark クラスで切り替える。
   App.vue の全要素トランジションと干渉しないよう、transition は各要素で明示する */
.theme-toggle {
  /* 見た目は 36px の円、ヒット領域は 44px 以上 */
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 44px;
  height: 44px;
  margin-right: -4px;
  padding: 0;
  background: transparent;
  border: 0;
  border-radius: 50%;
  cursor: pointer;
  color: #d97706; /* 太陽: 暖色のアンバー */
}
.theme-toggle.is-dark {
  color: var(--c-accent-text); /* 月 */
}
.theme-toggle:focus-visible {
  outline: 2px solid var(--c-accent);
  outline-offset: 2px;
}

.theme-toggle-disc {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: var(--c-bg-card);
  border: 1px solid var(--c-border);
  transition:
    background-color 0.25s ease,
    border-color 0.25s ease,
    transform 0.15s ease;
}
.theme-toggle:hover .theme-toggle-disc {
  background: var(--c-card-hover);
  border-color: var(--c-accent-border);
}
.theme-toggle:active .theme-toggle-disc {
  transform: scale(0.92);
}

/* 太陽(ライト)の状態が既定。ダークで月に変わる */
.theme-toggle-svg {
  display: block;
  transform: rotate(90deg);
  transition: transform 0.4s ease;
}
.theme-toggle.is-dark .theme-toggle-svg {
  transform: rotate(40deg);
}

.sun-moon {
  transform-origin: center center;
  transform: scale(0.55);
  transition: transform 0.4s ease;
}
.theme-toggle.is-dark .sun-moon {
  transform: scale(1);
}

/* マスク円: 太陽では本体の外へ退避、月では本体に重なって欠けを作る */
.moon-cut {
  transform: translate(16px, -3px);
  transition: transform 0.64s cubic-bezier(0.41, 0.64, 0.32, 1.575);
}
.theme-toggle.is-dark .moon-cut {
  transform: translate(0, 0);
}

/* 光線: 太陽では時計回りに順に現れ、月では即座に消える */
.sun-ray {
  transform-origin: center center;
  transform: scale(1);
  transition: transform 0.3s ease;
}
.sun-ray:nth-child(2) { transition-delay: 0.05s; }
.sun-ray:nth-child(3) { transition-delay: 0.1s; }
.sun-ray:nth-child(4) { transition-delay: 0.17s; }
.sun-ray:nth-child(5) { transition-delay: 0.25s; }
.sun-ray:nth-child(6) { transition-delay: 0.29s; }
.theme-toggle.is-dark .sun-ray {
  transform: scale(0);
  transition-delay: 0s;
}

@media (prefers-reduced-motion: reduce) {
  .theme-toggle-svg,
  .sun-moon,
  .moon-cut,
  .sun-ray,
  .theme-toggle-disc {
    transition-duration: 0s;
    transition-delay: 0s;
  }
  .theme-toggle:active .theme-toggle-disc {
    transform: none;
  }
}
</style>
